// member-portal/src/components/PromoterDashboard.jsx — FULL FILE (UPDATED)
// - Shows paid payouts as credit rows in Current Earnings Breakdown
// - Displays netEarnings (after credits)
// - Banner only shows active (pending/approved) requests
// - All layout preserved

import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { QRCodeCanvas } from 'qrcode.react';
import ReferralLinkGenerator from './promoter/ReferralLinkGenerator';
import { format } from 'date-fns';
import './PromoterDashboard.css';

const PromoterDashboard = ({ user, navigateTo }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [promoterData, setPromoterData] = useState(null);
  const qrRef = useRef(null);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [requestingPayout, setRequestingPayout] = useState(false);

  useEffect(() => {
    const fetchPromoterData = async () => {
      try {
        const token = localStorage.getItem('memberToken');
        if (!token) throw new Error('No authentication token found');

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/promoters/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || `Failed with status ${response.status}`);
        }

        const data = await response.json();
        setPromoterData(data);
      } catch (err) {
        console.error('[PromoterDashboard] Fetch error:', err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user?.role === 'Promoter') {
      fetchPromoterData();
    } else {
      setError('Access restricted to Promoters only');
      setLoading(false);
    }
  }, [user]);

  const toggleMonth = (monthKey) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  const requestPayout = async () => {
    if (requestingPayout) return;
    setRequestingPayout(true);

    try {
      const token = localStorage.getItem('memberToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/promoters/request-payout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to request payout');
      }

      toast.success('Payout request submitted successfully! Admin will review.');
      // Refresh data
      const refreshResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/promoters/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const refreshedData = await refreshResponse.json();
      setPromoterData(refreshedData);
    } catch (err) {
      console.error('[Request Payout] Error:', err);
      toast.error(err.message || 'Failed to submit payout request');
    } finally {
      setRequestingPayout(false);
    }
  };

  const copyQRToClipboard = async () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) {
      toast.error('Could not access QR code');
      return;
    }

    try {
      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error('Failed to create image');
          return;
        }
        const item = new ClipboardItem({ 'image/png': blob });
        await navigator.clipboard.write([item]);
        toast.success('QR image copied to clipboard!');
      }, 'image/png');
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      toast.error('Copy failed — try Save as PNG');
    }
  };

  const saveQRAsPNG = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) {
      toast.error('Could not access QR code');
      return;
    }

    const link = document.createElement('a');
    link.download = `Scover-Referral-QR-${promoterData.referralCode}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('QR image downloaded!');
  };

  if (loading) {
    return (
      <div className="promoter-loading">
        <div className="spinner"></div>
        <p>Loading your referral dashboard...</p>
      </div>
    );
  }

  if (error || !promoterData) {
    return (
      <div className="promoter-error">
        <h2>Error Loading Dashboard</h2>
        <p>{error || 'Unable to load promoter data. Please try again later.'}</p>
      </div>
    );
  }

  const referralUrl = `${window.location.origin}/signup?ref=${promoterData.referralCode}`;
  const rulesUrl = process.env.NODE_ENV === 'development'
    ? '/how-promoters-earn.html'
    : 'https://www.scover.co.za/how-promoters-earn.html';

  const hasActivePayout = promoterData.pendingPayoutRequest && 
    (promoterData.pendingPayoutRequest.status === 'pending' || 
     promoterData.pendingPayoutRequest.status === 'approved');

  const activePayout = promoterData.pendingPayoutRequest || { status: 'none' };

  // Combine current breakdown + paid payouts as credits
  const displayBreakdown = [
    ... (promoterData.earningsBreakdown || []),
    ... (promoterData.payoutHistory || [])
      .filter(p => p.status === 'paid')
      .map(p => ({
        companyName: 'Payout Processed',
        type: 'credit',
        revenue: null,
        amount: -p.requestedAmount,
        note: `Paid on ${new Date(p.paidAt).toLocaleDateString('en-ZA')}`
      }))
  ];

  return (
    <div className="promoter-dashboard">
      {/* Top: Welcome + Rules Button */}
      <div className="header-section">
        <h1>Welcome, {promoterData.name || promoterData.username || 'Promoter'}!</h1>
        <a
          href={rulesUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rules-button"
        >
          How Promoters Earn – Full Details & Rules
        </a>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Earnings (Gross)</h3>
          <p className="stat-value">R {promoterData.totalEarnings?.toFixed(2) || '0.00'}</p>
        </div>

        <div className="stat-card">
          <h3>Net Earnings</h3>
          <p className="stat-value">R {promoterData.netEarnings?.toFixed(2) || '0.00'}</p>
        </div>

        <div className="stat-card">
          <h3>Active Referrals</h3>
          <p className="stat-value">{promoterData.activeReferredCount || 0}</p>
        </div>
      </div>

      {/* Payout Status / Request Section */}
      <div style={{ textAlign: 'center', margin: '2.5rem 0' }}>
        {hasActivePayout ? (
          <div className={`payout-pending ${activePayout.status}`}>
            <h3>Payout Request {activePayout.status.toUpperCase()}</h3>
            <p>Amount: R{activePayout.requestedAmount.toFixed(2)}</p>
            <p>Requested: {new Date(activePayout.requestedAt).toLocaleString('en-ZA')}</p>
            {activePayout.status === 'approved' && (
              <p className="approved-note">Approved! Funds are being processed — expect payment soon.</p>
            )}
            {activePayout.status === 'pending' && (
              <p>Waiting for admin review. You cannot request again until this is processed.</p>
            )}
          </div>
        ) : (
          <div className="payout-info">
            <p>No pending or approved payout requests.</p>
            {promoterData.canRequestPayout ? (
              <button
                onClick={requestPayout}
                disabled={requestingPayout}
                className="payout-button"
              >
                {requestingPayout ? 'Submitting...' : 'Request Payout Now'}
              </button>
            ) : (
              <p>Total earnings must reach R500 to request payout.</p>
            )}
          </div>
        )}
      </div>

      {/* Current Earnings Breakdown (with payout credits) */}
      {displayBreakdown.length > 0 ? (
        <div className="earnings-breakdown">
          <h3>Current Earnings Breakdown (incl. Paid Payouts)</h3>
          <table className="breakdown-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Type</th>
                <th>Details</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {displayBreakdown.map((entry, idx) => (
                <tr key={idx}>
                  <td>{entry.companyName || (entry.type === 'credit' ? 'Payout Processed' : 'Unnamed')}</td>
                  <td>{entry.type === 'once-off' ? 'Once-off Bonus' : entry.type === 'monthly' ? 'Monthly Recurring' : 'Credit'}</td>
                  <td>
                    {entry.type === 'credit' 
                      ? entry.note 
                      : entry.type === 'once-off' 
                        ? 'Cumulative ≥ R500' 
                        : `R${entry.revenue?.toFixed(2) || 'N/A'}`}
                  </td>
                  <td className={entry.amount < 0 ? "amount credit" : "amount"}>
                    {entry.amount < 0 ? '-' : ''}R {Math.abs(entry.amount).toFixed(2)}
                  </td>
                </tr>
              ))}
              <tr className="total-row">
                <td>Net Total</td>
                <td></td>
                <td></td>
                <td className="amount">R {promoterData.netEarnings?.toFixed(2) || '0.00'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <p className="no-earnings">
          No earnings or payouts recorded yet. Share your link to start earning!
        </p>
      )}

      {/* Referral Link + QR */}
      <div className="referral-section">
        <h3>Your Referral Link & QR Code</h3>
        <div className="referral-content">
          <div className="referral-link-container">
            <ReferralLinkGenerator
              referralUrl={referralUrl}
              onCopySuccess={() => toast.success('Referral link copied!', { autoClose: 2000 })}
            />
          </div>

          <div className="qr-section">
            <div className="qr-container" ref={qrRef}>
              <QRCodeCanvas
                value={referralUrl}
                size={180}
                level="H"
                fgColor="#0f172a"
                bgColor="#ffffff"
              />
            </div>

            <div className="qr-actions">
              <button onClick={copyQRToClipboard} className="qr-btn copy">
                Copy QR Image
              </button>
              <button onClick={saveQRAsPNG} className="qr-btn save">
                Save as PNG
              </button>
            </div>

            <p className="qr-hint">
              Let restaurant owners scan this QR code to sign up using your referral link.
            </p>
          </div>
        </div>

        <p className="referral-hint">
          Share this link with restaurant owners — earn commission when they sign up and start generating revenue on Scover!
        </p>
      </div>

      {/* Historical Earnings */}
      {promoterData.historicalEarnings?.length > 0 && (
        <div className="historical-earnings">
          <h3>Earnings History</h3>
          {promoterData.historicalEarnings.map((hist, idx) => (
            <div key={idx} className="month-card">
              <div
                className="month-header"
                onClick={() => toggleMonth(hist.month)}
              >
                <h4>{format(new Date(hist.month), 'MMMM yyyy')}</h4>
                <span className="month-total">R {hist.totalRecurring.toFixed(2)}</span>
                <span className="toggle-icon">{expandedMonths[hist.month] ? '−' : '+'}</span>
              </div>

              {expandedMonths[hist.month] && (
                <table className="breakdown-table month-detail">
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th>Revenue</th>
                      <th>Rate</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hist.breakdown.map((entry, bIdx) => (
                      <tr key={bIdx}>
                        <td>{entry.companyName || 'Unnamed'}</td>
                        <td>R {entry.revenue.toFixed(2)}</td>
                        <td>{entry.rate}%</td>
                        <td className="amount">R {entry.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Referred Restaurants */}
      <div className="referred-orgs">
        <h2>Referred Restaurants</h2>
        {promoterData.referredOrgs?.length > 0 ? (
          <ul>
            {promoterData.referredOrgs.map(org => (
              <li key={org._id}>
                <strong>{org.companyName}</strong>
                <span> — Joined {new Date(org.createdAt).toLocaleDateString('en-ZA')}</span>
                <span className={org.isActive ? 'status-active' : 'status-inactive'}>
                  {org.isActive ? ' (Active)' : ' (Inactive)'}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p>No restaurants referred yet. Start sharing your link!</p>
        )}
      </div>
    </div>
  );
};

export default PromoterDashboard;